/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import { useMutationWithContext } from './useApolloWithContext';
import { CartAddMutation, CartAddMutationVariables } from '@exo/frontend-gql-generated-types/graphql';

export type CartAddMutationVariablesType = Omit<
  CartAddMutationVariables,
  'baseSiteId' | 'baseStoreId' | 'b2bUnitId'
> & { clearCart?: boolean | null };

export const CART_ADD = gql`
  mutation CartAdd(
    $baseSiteId: ID!
    $baseStoreId: ID!
    $b2bUnitId: ID!
    $input: [CrtAddToCartInput!]!
    $clearCart: Boolean
  ) {
    cartAdd(
      baseSiteId: $baseSiteId
      baseStoreId: $baseStoreId
      b2bUnitId: $b2bUnitId
      input: $input
      clearCart: $clearCart
    ) {
      wasSuccessful
      cart {
        id
        cartId
        cartType
        items {
          id
          entryNumber
          product {
            id
            productCode
            name
            volumePrices {
              id
              price {
                value
                currency
                formattedValue
              }
              formattedPrice
              uom
            }
            images {
              thumbnail {
                id
                imageType
                format
                url
                altText
                index
              }
              zoom {
                id
                imageType
                format
                url
                altText
                index
              }
              product {
                id
                imageType
                format
                url
                altText
                index
              }
            }
            EAN
            GTIN
            NDC
            uoms {
              id
              unit {
                id
                displayValue
                name
                code
              }
            }
          }
          quantity
          totalPrice {
            formattedValue
          }
          selectedUom {
            id
            displayValue
            code
          }
          totalWeight
          listPrice {
            id
            price {
              formattedValue
            }
            formattedPrice
          }
          customerPrice {
            id
            price {
              formattedValue
            }
            formattedPrice
          }
        }
        totals {
          id
          subtotal {
            formattedValue
          }
          total {
            formattedValue
          }
          currency {
            isoCode
            name
          }
          totalWeight {
            value
            uom {
              id
              displayValue
            }
          }
        }
        ruleWarnings {
          __typename
          ... on CrtMinWeightWarning {
            id
            message
            threshold
          }
          ... on CrtMinValueWarning {
            id
            message
            threshold
          }
          ... on CrtMaxValueWarning {
            id
            message
            threshold
          }
          ... on CrtMinQtyWarning {
            id
            message
            threshold
          }
        }
        ruleErrors {
          __typename
          ... on CrtMinWeightError {
            id
            message
            threshold
          }
          ... on CrtMinValueError {
            id
            message
            threshold
          }
          ... on CrtMaxValueError {
            id
            message
            threshold
          }
          ... on CrtMinQtyError {
            id
            message
            threshold
          }
        }
      }
      addToCartWarnings {
        __typename
        ... on CrtDuplicateLineItemWarning {
          id
          message
        }
      }
      addToCartErrors {
        __typename
        ... on CrtDuplicateLineItemError {
          id
          message
        }
      }
    }
  }
`;

export const useAddToCartMutation = () => {
  return useMutationWithContext<CartAddMutation, CartAddMutationVariablesType>(CART_ADD);
};
